import React from "react";
import { motion } from "framer-motion";
import { LucideIcon } from "lucide-react";

interface OptionItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  emoji?: string;
  description?: string;
}

interface LanguageOnboardingStepProps {
  stepNumber: number;
  totalSteps: number;
  title: string;
  subtitle?: string;
  options: OptionItem[];
  multiSelect?: boolean;
  selectedIds: string[];
  onSelect: (id: string) => void;
}

const LanguageOnboardingStep: React.FC<LanguageOnboardingStepProps> = ({
  stepNumber,
  totalSteps,
  title,
  subtitle,
  options,
  multiSelect = false,
  selectedIds,
  onSelect,
}) => {
  const handleSelect = (id: string) => {
    onSelect(id);
  };

  return (
    <div className="w-full">
      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div
            key={i}
            className="rounded-full transition-all duration-300"
            style={{
              width: i + 1 === stepNumber ? 20 : 6,
              height: 6,
              background:
                i + 1 < stepNumber
                  ? "hsl(140 60% 50%)"
                  : i + 1 === stepNumber
                    ? "hsl(187 72% 50%)"
                    : "hsl(187 72% 50% / 0.15)",
              borderRadius: 3,
            }}
          />
        ))}
      </div>

      {/* Title & subtitle */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="text-center mb-6"
      >
        <h2 className="font-display text-[22px] font-bold text-foreground leading-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="text-[13px] font-body text-foreground/50 mt-2 max-w-xs mx-auto leading-relaxed">
            {subtitle}
          </p>
        )}
      </motion.div>

      {/* Options grid */}
      <div className="grid grid-cols-1 gap-2.5">
        {options.map((opt, i) => {
          const isSelected = selectedIds.includes(opt.id);
          return (
            <motion.button
              key={opt.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.06 + i * 0.05, duration: 0.4 }}
              onClick={() => handleSelect(opt.id)}
              className="w-full text-left rounded-2xl p-4 transition-all active:scale-[0.97]"
              whileHover={{ scale: 1.01 }}
              style={{
                background: isSelected
                  ? "hsl(187 72% 50% / 0.08)"
                  : "hsl(var(--card) / 0.7)",
                border: `1.5px solid ${isSelected ? "hsl(187 72% 50% / 0.5)" : "hsl(var(--border) / 0.3)"}`,
                boxShadow: isSelected
                  ? "0 0 20px hsl(187 72% 50% / 0.08), inset 0 1px 0 hsl(187 72% 50% / 0.06)"
                  : "none",
                backdropFilter: "blur(12px)",
              }}
            >
              <div className="flex items-center gap-3">
                {/* Icon or Emoji */}
                {(opt.icon || opt.emoji) && (
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: isSelected
                        ? "hsl(187 72% 50% / 0.12)"
                        : "hsl(187 72% 50% / 0.06)",
                      border: `1px solid ${isSelected ? "hsl(187 72% 50% / 0.25)" : "hsl(187 72% 50% / 0.1)"}`,
                    }}
                  >
                    {opt.emoji ? (
                      <span className="text-[20px] leading-none">{opt.emoji}</span>
                    ) : opt.icon ? (
                      <opt.icon
                        size={18}
                        style={{
                          color: isSelected ? "hsl(187 72% 55%)" : "hsl(218 14% 55%)",
                        }}
                      />
                    ) : null}
                  </div>
                )}

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <p
                    className="font-display text-[14px] font-semibold"
                    style={{
                      color: isSelected ? "hsl(187 72% 65%)" : "hsl(var(--foreground))",
                    }}
                  >
                    {opt.label}
                  </p>
                  {opt.description && (
                    <p className="text-[11px] font-body text-foreground/45 mt-0.5 truncate">
                      {opt.description}
                    </p>
                  )}
                </div>

                {/* Selection indicator */}
                <div
                  className="w-5 h-5 rounded-full shrink-0 flex items-center justify-center transition-all"
                  style={{
                    border: `1.5px solid ${isSelected ? "hsl(187 72% 50%)" : "hsl(var(--border) / 0.4)"}`,
                    background: isSelected ? "hsl(187 72% 50%)" : "transparent",
                  }}
                >
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-2 h-2 rounded-full bg-background"
                    />
                  )}
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default LanguageOnboardingStep;
